import { ArchitectureParseError } from "../errors";
import { splitH3Entries } from "../parse-sections";
import type { Component, ComponentKind } from "../types";
import { parseAssumptions } from "./parse-assumptions-v3";
import { asString, asStringArray, parseBoldEntriesV3 } from "./parse-bullets-v3";
import { parseRisks } from "./parse-risks-v3";

// ## L3 — Components
// ### Components    (flat list — each component carries `container: <slug>`
//                    instead of nesting under a Container parent)

const COMPONENT_KIND_VALUES: ReadonlySet<ComponentKind> = new Set([
  "module",
  "service-class",
  "worker-type",
  "route-group",
  "data-model",
  "integration-adapter",
  "other",
]);

export function parseL3(body: string): Component[] {
  const components: Component[] = [];
  for (const sub of splitH3Entries(body)) {
    const title = sub.title.toLowerCase().trim();
    if (title === "components") {
      components.push(...parseComponents(sub.body));
    }
  }
  return components;
}

function parseComponents(body: string): Component[] {
  const entries = parseBoldEntriesV3(body, "L3.Components");
  return entries.map((entry) => {
    const p = entry.properties;
    const scope = `L3.Components.${entry.id}`;
    const container = asString(p.container);
    if (!container) {
      throw new ArchitectureParseError(
        `${scope}: \`container\` is required — every component must reference its container by slug. v0.3 flattens components to the top level; the parent link lives on the entry itself, not in the heading structure.`,
      );
    }
    const rawKind = (asString(p.kind) ?? "").toLowerCase();
    const kind: ComponentKind | undefined =
      rawKind === ""
        ? undefined
        : COMPONENT_KIND_VALUES.has(rawKind as ComponentKind)
          ? (rawKind as ComponentKind)
          : "other";
    const tech_stack = asStringArray(p.tech_stack);
    const risks = parseRisks(p.risks, { scope, strict: true });
    const assumptions = parseAssumptions(p.assumptions, {
      scope,
      strict: true,
    });
    return {
      id: entry.id,
      label: asString(p.label) ?? entry.id,
      headline: asString(p.headline),
      description: asString(p.description),
      why: asString(p.why),
      kind,
      container,
      tech_stack,
      risks,
      assumptions,
      // Legacy compat — renderer still reads these fields directly until
      // Phase 5.
      containerId: container,
      technology: tech_stack,
      risk: risks.map((r) => r.description),
    };
  });
}
